// src/pages/Admin/Verifications/VerificationsPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminAPI } from '../../../api/admin';
import { addressAPI } from '../../../api/addresses';
import type { AddressVerification, IdentityVerification, PaginatedResponse } from '../../../types';
import { AddressDetail } from '../../../components/admin/AddressDetail';
import { IdentityDetail } from '../../../components/admin/IdentityDetail';
import IdentityItem from '../../../components/admin/IdentityItem';
import AddressItem from '../../../components/verification/AddressItem';

type VerificationType = 'identity' | 'address';
type VerificationStatus = 'pending' | 'approved' | 'rejected' | 'needs_location';

const VerificationsPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<VerificationType>('identity');
    const [statusFilter, setStatusFilter] = useState<VerificationStatus | 'all'>('pending');
    const [loading, setLoading] = useState(true);
    const [selectedIdentity, setSelectedIdentity] = useState<IdentityVerification | null>(null);
    const [selectedAddress, setSelectedAddress] = useState<AddressVerification | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const isInitialMount = useRef(true);
    const [pagination, setPagination] = useState({
        count: 0,
        next: null as string | null,
        previous: null as string | null,
        currentPage: 1
    });

    const statusFilters: { value: VerificationStatus | 'all'; label: string }[] = [
        { value: 'all', label: 'All' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'needs_location', label: 'Needs Location' },
    ];

    const [data, setData] = useState<{
        identity: IdentityVerification[];
        address: AddressVerification[];
    }>({
        identity: [],
        address: []
    });

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery) {
                handleSearch();
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);


    // Fetch data when dependencies change
    useEffect(() => {
        // Skip the first run if needed
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        if (searchQuery) {
            handleSearch();
        } else {
            fetchData();
        }
    }, [activeTab, statusFilter, pagination.currentPage, searchQuery]);

    const handleTabChange = (tab: VerificationType) => {
        setActiveTab(tab);
        setStatusFilter('pending');
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            // Handle 'all' status - fetch all statuses and combine
            if (statusFilter === 'all') {
                if (activeTab === 'identity') {
                    // Fetch all identity statuses
                    const [pendingRes, approvedRes, rejectedRes] = await Promise.all([
                        adminAPI.getIdentityVerifications('pending', 1),
                        adminAPI.getIdentityVerifications('approved', 1),
                        adminAPI.getIdentityVerifications('rejected', 1)
                    ]);

                    // Combine all results with safe checks
                    const allIdentities = [
                        ...(pendingRes.data?.results || []),
                        ...(approvedRes.data?.results || []),
                        ...(rejectedRes.data?.results || [])
                    ];

                    setData(prev => ({
                        ...prev,
                        identity: allIdentities
                    }));

                    // Calculate total count from all responses
                    const totalCount = (pendingRes.data?.count || 0) +
                        (approvedRes.data?.count || 0) +
                        (rejectedRes.data?.count || 0);

                    setPagination({
                        count: totalCount,
                        next: null, // Pagination disabled for 'all' view
                        previous: null,
                        currentPage: 1
                    });
                } else {
                    // Fetch all address statuses
                    const [pendingRes, approvedRes, rejectedRes, needsLocationRes] = await Promise.all([
                        adminAPI.getAddressVerifications('pending', 1),
                        adminAPI.getAddressVerifications('approved', 1),
                        adminAPI.getAddressVerifications('rejected', 1),
                        adminAPI.getAddressVerifications('needs_location', 1)
                    ]);

                    // Combine all results with safe checks
                    const allAddresses = [
                        ...(pendingRes.data?.results || []),
                        ...(approvedRes.data?.results || []),
                        ...(rejectedRes.data?.results || []),
                        ...(needsLocationRes.data?.results || [])
                    ];

                    setData(prev => ({
                        ...prev,
                        address: allAddresses
                    }));

                    // Calculate total count from all responses
                    const totalCount = (pendingRes.data?.count || 0) +
                        (approvedRes.data?.count || 0) +
                        (rejectedRes.data?.count || 0) +
                        (needsLocationRes.data?.count || 0);

                    setPagination({
                        count: totalCount,
                        next: null,
                        previous: null,
                        currentPage: 1
                    });
                }
            }
            // Handle specific status with pagination
            else if (activeTab === 'identity') {
                const response = await adminAPI.getIdentityVerifications(
                    statusFilter as 'pending' | 'approved' | 'rejected',
                    pagination.currentPage
                );
                if (!response.error && response.data) {
                    setData(prev => ({
                        ...prev,
                        identity: response.data?.results || []
                    }));
                    setPagination({
                        count: response.data.count || 0,
                        next: response.data.next || null,
                        previous: response.data.previous || null,
                        currentPage: pagination.currentPage
                    });
                } else {
                    // Handle error or empty response
                    setData(prev => ({ ...prev, identity: [] }));
                    setPagination(prev => ({
                        ...prev,
                        count: 0,
                        next: null,
                        previous: null
                    }));
                }
            }
            // Address tab with specific status
            else {
                const response = await adminAPI.getAddressVerifications(
                    statusFilter as 'pending' | 'approved' | 'rejected' | 'needs_location',
                    pagination.currentPage
                );

                if (!response.error && response.data) {
                    setData(prev => ({
                        ...prev,
                        address: response.data.results || []
                    }));
                    setPagination({
                        count: response.data.count || 0,
                        next: response.data.next || null,
                        previous: response.data.previous || null,
                        currentPage: pagination.currentPage
                    });
                } else {
                    // Handle error or empty response
                    setData(prev => ({ ...prev, address: [] }));
                    setPagination(prev => ({
                        ...prev,
                        count: 0,
                        next: null,
                        previous: null
                    }));
                }
            }
        } catch (error) {
            console.error('Failed to fetch verifications:', error);
            // Reset data on error
            setData({ identity: [], address: [] });
            setPagination({
                count: 0,
                next: null,
                previous: null,
                currentPage: 1
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            fetchData();
            return;
        }

        setLoading(true);
        try {
            const response = await adminAPI.searchVerifications(searchQuery, activeTab);
            if (!response.error && response.data) {
                setData({
                    identity: response.data.identity || [],
                    address: response.data.address || []
                });
            }
        } catch (error) {
            console.error('Search failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleIdentityVerify = async (id: number, approved: boolean, reason?: string) => {
        await adminAPI.verifyIdentity(id, approved, reason);
        await fetchData();
        setSelectedIdentity(null);
    };

    const handleAddressVerify = async (id: number, approved: boolean, reason?: string) => {
        await addressAPI.verifyAddress(id, approved, reason);
        await fetchData();
        setSelectedAddress(null);
    };

    const handlePageChange = (direction: 'next' | 'prev') => {
        if (direction === 'next' && pagination.next) {
            setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
        } else if (direction === 'prev' && pagination.previous) {
            setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
        }
    };

    const filterBySearch = (items: any[] = []) => {
        if (!searchQuery.trim() || !items) return items || [];
        return items; // Search is now handled by API
    };

    const getFilteredData = () => {
        const items = activeTab === 'identity' ? (data.identity || []) : (data.address || []);
        console.log('Filtering items:', items, 'Status Filter:', statusFilter, 'Search Query:', searchQuery);
        // If statusFilter is 'all', return all items without status filtering
        if (statusFilter === 'all') {
            return filterBySearch(items);
        }
        // Apply status filtering
        let statusFiltered = items;
        if (activeTab === 'identity') {
            statusFiltered = items.filter(
                item => (item as IdentityVerification).id_verification_status === statusFilter
            );
        } else {
            // Address tab - handle needs_location specially
            if (statusFilter === 'needs_location') {
                statusFiltered = items.filter(
                    item => (item as AddressVerification).address_verification_status === 'needs_location'
                );
            } else {
                statusFiltered = items.filter(
                    item => (item as AddressVerification).address_verification_status === statusFilter
                );
            }
        }

        // Then filter by search
        return filterBySearch(statusFiltered);
    };

    const getCountsByStatus = () => {
        // Add safe checks with default empty arrays
        const identityItems = data.identity || [];
        const addressItems = data.address || [];

        if (activeTab === 'identity') {
            return {
                pending: identityItems.filter(i => i?.id_verification_status === 'pending').length,
                approved: identityItems.filter(i => i?.id_verification_status === 'approved').length,
                rejected: identityItems.filter(i => i?.id_verification_status === 'rejected').length,
                needs_location: 0
            };
        } else {
            return {
                pending: addressItems.filter(a => a?.address_verification_status === 'pending' && a?.latitude).length,
                approved: addressItems.filter(a => a?.address_verification_status === 'approved').length,
                rejected: addressItems.filter(a => a?.address_verification_status === 'rejected').length,
                needs_location: addressItems.filter(a => a?.address_verification_status === 'needs_location' && !a?.latitude).length
            };
        }
    };

    const counts = getCountsByStatus();
    const filteredData = getFilteredData();

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                    <ShieldCheck className="mr-2" size={28} />
                    Verifications
                </h1>
                <p className="text-gray-600 mt-1">
                    Review and verify user identities and addresses
                </p>
            </div>

            {/* Search Bar */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="flex -mb-px space-x-8">
                    <button
                        onClick={() => handleTabChange('identity')}
                        className={`
                            py-3 px-1 border-b-2 font-medium text-sm transition-colors
                            ${activeTab === 'identity'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }
                        `}
                    >
                        Identity Verification
                        {counts.pending > 0 && activeTab === 'identity' && (
                            <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                {counts.pending}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={() => handleTabChange('address')}
                        className={`
                            py-3 px-1 border-b-2 font-medium text-sm transition-colors
                            ${activeTab === 'address'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }
                        `}
                    >
                        Address Verification
                        {counts.pending > 0 && activeTab === 'address' && (
                            <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                {counts.pending}
                            </span>
                        )}
                    </button>
                </nav>
            </div>

            {/* Status Filters */}
            <div className="flex space-x-2">
                {statusFilters.map((filter) => {
                    if (activeTab === 'identity' && filter.value === 'needs_location') return null;

                    const count = counts[filter.value as keyof typeof counts] || 0;

                    return (
                        <button
                            key={filter.value}
                            onClick={() => {
                                setStatusFilter(filter.value);
                                setPagination(prev => ({ ...prev, currentPage: 1 }));
                            }}
                            className={`px-3 py-1 rounded-full text-sm capitalize ${statusFilter === filter.value
                                ? filter.value === 'all'
                                    ? 'bg-gray-800 text-white'
                                    : filter.value === 'pending'
                                        ? 'bg-yellow-500 text-white'
                                        : filter.value === 'approved'
                                            ? 'bg-green-500 text-white'
                                            : filter.value === 'rejected'
                                                ? 'bg-red-500 text-white'
                                                : 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            {filter.label}
                            {filter.value !== 'all' && count > 0 && (
                                <span className="ml-1 text-xs opacity-75">
                                    ({count})
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Results count and Pagination */}
            <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">
                    Showing {filteredData.length} of {pagination.count} results
                </p>

                {statusFilter !== 'pending' && (
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => handlePageChange('prev')}
                            disabled={!pagination.previous}
                            className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <span className="text-sm text-gray-600">
                            Page {pagination.currentPage} of {Math.ceil(pagination.count / 20)}
                        </span>
                        <button
                            onClick={() => handlePageChange('next')}
                            disabled={!pagination.next}
                            className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                )}
            </div>

            {/* Verifications List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="text-gray-500 mt-2">Loading verifications...</p>
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="text-center py-12">
                        <ShieldCheck size={48} className="mx-auto text-gray-400 mb-3" />
                        <p className="text-gray-500">No {statusFilter !== 'all' ? statusFilter : ''} verifications found</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-200">
                        {(filteredData || []).map((item) => (
                            <div key={item.id}>
                                {activeTab === 'identity' ? (
                                    <IdentityItem
                                        item={item as IdentityVerification}
                                        onVerify={handleIdentityVerify}
                                        onClick={() => setSelectedIdentity(item as IdentityVerification)}
                                        showStatusBadge={statusFilter === 'all'}
                                    />
                                ) : (
                                    <AddressItem
                                        item={item as AddressVerification}
                                        onVerify={handleAddressVerify}
                                        showStatusBadge={statusFilter === 'all'}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Detail Modals */}
            {selectedIdentity && (
                <IdentityDetail
                    item={selectedIdentity}
                    onClose={() => setSelectedIdentity(null)}
                    onVerify={handleIdentityVerify}
                />
            )}

            {selectedAddress && (
                <AddressDetail
                    item={selectedAddress}
                    onClose={() => setSelectedAddress(null)}
                    onVerify={handleAddressVerify}
                />
            )}
        </div>
    );
};

export default VerificationsPage;